import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// ---------------------------------------------------------------------------
// Head-tag reconciliation for Helmet on hydrated crawler-cache HTML.
//
// Emergent's crawler-cache renders this SPA in headless Chromium and then
// serializes the DOM back to HTML. During that serialization it STRIPS the
// `data-rh` attribute that react-helmet-async marks its managed tags with.
// Bots receive a head that contains `<title>`, `<meta name="description">`,
// `<link rel="canonical">` etc. WITHOUT `data-rh`. When Bing (or any user)
// then re-executes the bundle, react-helmet-async 3.x scans for tags with
// `data-rh` to reconcile against — finds none — and APPENDS a second copy
// of every managed tag, surfacing in Bing WMT as
// "More than one title / meta description / canonical tag".
//
// Fix: right before React mounts, drop every head element Helmet would
// otherwise create afresh, but only when the element carries no `data-rh`.
// Helmet then re-emits exactly one instance per tag. Non-JS crawlers never
// reach this code, so they keep consuming the untouched crawler-cache HTML.
// ---------------------------------------------------------------------------
if (typeof document !== "undefined" && document.head) {
  const HELMET_MANAGED_SELECTORS = [
    'title:not([data-rh])',
    'meta[name="description"]:not([data-rh])',
    'meta[name="robots"]:not([data-rh])',
    'meta[name^="twitter:"]:not([data-rh])',
    'meta[property^="og:"]:not([data-rh])',
    'link[rel="canonical"]:not([data-rh])',
    'link[rel="alternate"][hreflang]:not([data-rh])',
    'script[type="application/ld+json"]:not([data-rh])',
  ];
  try {
    HELMET_MANAGED_SELECTORS.forEach((sel) => {
      document.head.querySelectorAll(sel).forEach((n) => n.remove());
    });
  } catch (_) {
    /* never block the app on this housekeeping */
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
